import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { Columns3 } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import {
  createDonorEquipment,
  createEquipmentCategoryMaster,
  createEquipmentConditionMaster,
  createEquipmentAllocation,
  updateEquipmentAllocation,
  deleteEquipmentAllocation,
  createEquipmentStatusMaster,
  deleteEquipmentCategoryMaster,
  deleteEquipmentConditionMaster,
  deleteDonorEquipment,
  deleteEquipmentStatusMaster,
  getEquipmentCategoryMasters,
  getEquipmentConditionMasters,
  getDonorEquipmentDashboard,
  getDonorEquipmentList,
  getDonorEquipmentLookups,
  getEquipmentAllocations,
  getEquipmentStatusMasters,
  bulkSetEquipmentDonationVerified,
  updateDonorEquipment,
  updateEquipmentCategoryMaster,
  updateEquipmentConditionMaster,
  updateEquipmentStatusMaster,
} from "../api/donorEquipmentApi";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import GlobalDataTable from "../../../global/GlobalDataTable";
import GlobalBadge from "../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import {
  GlobalButton,
  GlobalDateField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { GlobalDirectoryPaginationSection } from "../../../global/directory";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/VolunteerTable.css";
import "../styles/MembershipApplications.css";
import { masterSelectOptions } from "../../memberships/utils/masterLookupUtils";

const ALERT_ID = "equipment-form-alert";
const COLUMN_VISIBILITY_KEY = "girinestham.donorEquipmentList.columnVisibility";
const COLUMN_DEFS = [
  { id: "equipment_code", label: "Code", defaultVisible: true },
  { id: "donation_date", label: "Date", defaultVisible: true },
  { id: "donor_name", label: "Donor", defaultVisible: true },
  { id: "equipment_name", label: "Equipment", defaultVisible: true },
  { id: "category_name", label: "Category", defaultVisible: true },
  { id: "quantity", label: "Qty", defaultVisible: true },
  { id: "estimated_value", label: "Value", defaultVisible: true },
  { id: "donation_verified", label: "Donation verified", defaultVisible: true },
  { id: "location", label: "Location", defaultVisible: true },
  { id: "equipment_status_name", label: "Status", defaultVisible: true },
];
const ALLOCATION_COLUMN_DEFS = [
  { id: "allocation_code", label: "Allocation Code", defaultVisible: true },
  { id: "equipment_name", label: "Equipment", defaultVisible: true },
  { id: "quantity", label: "Qty", defaultVisible: true },
  { id: "assigned_to", label: "Assigned To", defaultVisible: true },
  { id: "allocation_date", label: "Allocation Date", defaultVisible: true },
  { id: "remarks", label: "Remarks", defaultVisible: true },
];

const EMPTY_FILTERS = {
  from_date: "",
  to_date: "",
};

const EMPTY_EQUIPMENT_FORM = {
  donor_id: "",
  donor_name_input: "",
  donation_id: "",
  donation_date: format(new Date(), "yyyy-MM-dd"),
  equipment_name: "",
  category_id: "",
  quantity: "",
  estimated_value: "",
  brand: "",
  model: "",
  condition_id: "",
  location: "",
  equipment_status_id: "",
  remarks: "",
};

const EMPTY_ALLOCATION_FORM = {
  equipment_id: "",
  equipment_name_input: "",
  quantity: "",
  assigned_to: "",
  allocation_date: format(new Date(), "yyyy-MM-dd"),
  remarks: "",
};

function safeErrorText(err, fallback = "Something went wrong. Please try again.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

/** Record status column: pill badge (mst_statuses: 1 = active; 2 = inactive). */
function MasterRecordStatusBadge({ statusId, statusLabel }) {
  const active =
    statusId == null || statusId === "" || Number(statusId) === 1;
  const raw = String(statusLabel ?? "").trim();
  const label = raw ? raw.toUpperCase() : active ? "ACTIVE" : "INACTIVE";
  return <GlobalBadge active={active} activeText={label} inactiveText={label} />;
}

function formatDateCell(value) {
  if (!value) return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? `₹${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
    : "—";
}
function donationVerifiedLabel(row) {
  return Number(row?.donation_verified) === 1 ? "Verified" : "Not verified";
}

const EQUIPMENT_NAME_REGEX = /^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/;
function sanitizePositiveNumberInput(raw) {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, "")}`;
  }
  return s;
}
function sanitizePositiveIntegerInput(raw) {
  return String(raw ?? "").replace(/[^\d]/g, "");
}

function validateEquipmentForm(formData) {
  const errors = {};
  if (!formData.donor_id) errors.donor_id = "Donor is required";
  if (!formData.donation_date) errors.donation_date = "Donation date is required";
  const equipmentName = String(formData.equipment_name || "").trim();
  if (!equipmentName) {
    errors.equipment_name = "Equipment name is required";
  } else if (equipmentName.length < 4 || !EQUIPMENT_NAME_REGEX.test(equipmentName)) {
    errors.equipment_name = "Minimum 4 characters; only letters, numbers, and single spaces allowed";
  }
  if (!formData.category_id) errors.category_id = "Category is required";
  if (!Number(formData.quantity) || Number(formData.quantity) < 1) errors.quantity = "Quantity must be a number and at least 1";
  if (!Number(formData.estimated_value) || Number(formData.estimated_value) < 1) errors.estimated_value = "Estimated value must be a number and at least 1";
  if (!formData.condition_id) errors.condition_id = "Condition is required";
  if (!formData.equipment_status_id) errors.equipment_status_id = "Status is required";
  return errors;
}

function validateAllocationForm(formData) {
  const errors = {};
  if (!formData.equipment_id) errors.equipment_id = "Equipment is required";
  if (!Number(formData.quantity) || Number(formData.quantity) < 1) errors.quantity = "Quantity must be a number and at least 1";
  if (!String(formData.assigned_to || "").trim()) errors.assigned_to = "Assigned to is required";
  if (!formData.allocation_date) errors.allocation_date = "Allocation date is required";
  return errors;
}

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore localStorage errors
  }
  return null;
}

function renderExportCell(row, colId) {
  if (colId === "donation_date") return formatDateCell(row.donation_date);
  if (colId === "estimated_value") return formatMoney(row.estimated_value);
  if (colId === "donation_verified") return donationVerifiedLabel(row);
  return row?.[colId] ?? "—";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function exportEquipmentCsv(rows, visibleColumns) {
  const headers = visibleColumns.map((c) => c.label);
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    const values = visibleColumns.map((c) => renderExportCell(r, c.id)).map((v) => {
      const s = String(v ?? "");
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, "\"\"")}"`;
      return s;
    });
    lines.push(values.join(","));
  });

  const blob = new Blob(["\ufeff", lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  a.download = `donor-equipments-${y}-${m}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function renderAllocationCell(row, colId) {
  if (colId === "allocation_date") return formatDateCell(row.allocation_date);
  if (colId === "allocation_code") return row?.allocation_code || "—";
  return row?.[colId] ?? "—";
}

function exportAllocationCsv(rows, visibleColumns) {
  const headers = visibleColumns.map((c) => c.label);
  const lines = [headers.join(",")];
  (rows || []).forEach((r) => {
    const values = visibleColumns.map((c) => renderAllocationCell(r, c.id)).map((v) => {
      const s = String(v ?? "");
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, "\"\"")}"`;
      return s;
    });
    lines.push(values.join(","));
  });
  const blob = new Blob(["\ufeff", lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  a.download = `equipment-allocations-${y}-${m}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportEquipmentPdf(rows, visibleColumns) {
  if (!visibleColumns.length) return;

  const headerHtml = visibleColumns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("");
  const bodyHtml = rows
    .map((r) => {
      const cells = visibleColumns
        .map((c) => `<td>${escapeHtml(renderExportCell(r, c.id))}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const w = window.open("", "_blank", "noopener,noreferrer,width=1080,height=860");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Donor Equipment Export</title>
    <style>
      body{font-family:system-ui;padding:20px;}
      table{border-collapse:collapse;width:100%;}
      th,td{border:1px solid #d1d5db;padding:8px;font-size:12px;text-align:left;vertical-align:top;}
      th{background:#f3f4f6;}
      h2{margin:0 0 10px 0;}
    </style>
  </head><body>
    <h2>Donor Equipment Export</h2>
    <p>Generated on ${escapeHtml(new Date().toLocaleString())}</p>
    <table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
    <script>window.onload=function(){window.print();};</script>
  </body></html>`);
  w.document.close();
}

export default function DonorEquipmentListPage({ embedMode = null, onEquipmentDataChanged } = {}) {
  const isAdminRoute = getJwtRoleSlug() === "admin";
  const hideEmbeddedChrome = Boolean(embedMode);
  const hideEquipmentStatsPanel = Boolean(embedMode);
  const hideAllocationAndMasters = Boolean(embedMode);
  const equipmentTrackingEditOnly = embedMode === "donations-tracking";
  const hideEquipmentListPanel = embedMode === "donations-collect";
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [lookups, setLookups] = useState({ categories: [], conditions: [], statuses: [], master_statuses: [], donors: [] });
  const [allocations, setAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [formData, setFormData] = useState(EMPTY_EQUIPMENT_FORM);
  const [allocationForm, setAllocationForm] = useState(EMPTY_ALLOCATION_FORM);
  const [editingAllocationId, setEditingAllocationId] = useState(null);
  const [equipmentErrors, setEquipmentErrors] = useState({});
  const [allocationErrors, setAllocationErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const hideEquipmentFormPanel = embedMode === "donations-tracking" && !editingRow;
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [submittingEquipment, setSubmittingEquipment] = useState(false);
  const [submittingAllocation, setSubmittingAllocation] = useState(false);
  const [masterSubmitting, setMasterSubmitting] = useState(false);
  const [categoriesMasterRows, setCategoriesMasterRows] = useState([]);
  const [conditionsMasterRows, setConditionsMasterRows] = useState([]);
  const [statusesMasterRows, setStatusesMasterRows] = useState([]);
  const [categoryMaster, setCategoryMaster] = useState({ id: null, category_name: "", description: "", status_id: "1" });
  const [conditionMaster, setConditionMaster] = useState({ id: null, condition_name: "", description: "", status_id: "1" });
  const [statusMaster, setStatusMaster] = useState({ id: null, status_name: "", status_id: "1" });
  const [categoryMasterErrors, setCategoryMasterErrors] = useState({});
  const [conditionMasterErrors, setConditionMasterErrors] = useState({});
  const [statusMasterErrors, setStatusMasterErrors] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const [allocationFilterColumn, setAllocationFilterColumn] = useState("");
  const [allocationFilterValue, setAllocationFilterValue] = useState("");
  const [allocationPagination, setAllocationPagination] = useState({ page: 1, limit: 10 });
  const [allocationColumnPickerOpen, setAllocationColumnPickerOpen] = useState(false);
  const allocationPickerRef = useRef(null);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState(new Set());
  const [bulkMarkingEquipment, setBulkMarkingEquipment] = useState(false);
  const [allocationColumnVisibility, setAllocationColumnVisibility] = useState(() => {
    const initial = {};
    ALLOCATION_COLUMN_DEFS.forEach((c) => {
      initial[c.id] = c.defaultVisible;
    });
    return initial;
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    COLUMN_DEFS.forEach((c) => {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    });
    return initial;
  });

  const donorSuggestionList = useMemo(() => {
    const q = String(formData.donor_name_input || "").trim().toLowerCase();
    const donors = lookups.donors || [];
    if (!q) return donors.slice(0, 8);
    return donors
      .filter((d) =>
        String(d.donor_name || "").toLowerCase().includes(q) ||
        String(d.donor_code || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [lookups.donors, formData.donor_name_input]);
  const equipmentSuggestionList = useMemo(() => {
    const q = String(allocationForm.equipment_name_input || "").trim().toLowerCase();
    if (!q) return rows.slice(0, 8);
    return (rows || [])
      .filter((r) =>
        String(r.equipment_name || "").toLowerCase().includes(q) ||
        String(r.equipment_code || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [rows, allocationForm.equipment_name_input]);
  const categoryOptions = useMemo(
    () =>
      masterSelectOptions(lookups.categories || [], {
        labelKey: "category_name",
        selectedValue: formData.category_id,
      }).map((o) => ({ value: String(o.value), label: o.label })),
    [lookups.categories, formData.category_id],
  );
  const conditionOptions = useMemo(
    () =>
      masterSelectOptions(lookups.conditions || [], {
        labelKey: "condition_name",
        selectedValue: formData.condition_id,
      }).map((o) => ({ value: String(o.value), label: o.label })),
    [lookups.conditions, formData.condition_id],
  );
  const statusOptions = useMemo(
    () =>
      masterSelectOptions(lookups.statuses || [], {
        labelKey: "status_name",
        selectedValue: formData.equipment_status_id,
      }).map((o) => ({ value: String(o.value), label: o.label })),
    [lookups.statuses, formData.equipment_status_id],
  );
  const masterStatusOptions = useMemo(
    () => (lookups.master_statuses || []).map((s) => ({ value: String(s.id), label: s.status_name })),
    [lookups.master_statuses],
  );

  const QUICK_JUMP_ITEMS = useMemo(() => {
    const items = [];
    if (!hideEquipmentStatsPanel) {
      items.push({ key: "stats", label: "Dashboard", targetId: "equipment-stats" });
    }
    items.push({ key: "form", label: "Add / edit", targetId: "equipment-form" });
    if (!hideEquipmentListPanel) {
      items.push({ key: "list", label: "Equipment list", targetId: "equipment-list" });
    }
    if (!hideAllocationAndMasters) {
      items.push({ key: "allocations", label: "Allocation", targetId: "equipment-allocation" });
      items.push({ key: "masters", label: "Master tables", targetId: "equipment-masters" });
    }
    return items;
  }, [hideEquipmentStatsPanel, hideEquipmentListPanel, hideAllocationAndMasters]);
  const visibleDataColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );
  const tableHeaders = useMemo(
    () => [
      ...(isAdminRoute ? [{ key: "select", label: "", className: "checkbox-cell" }] : []),
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [visibleDataColumns, isAdminRoute],
  );
  const allocationVisibleColumns = useMemo(
    () => ALLOCATION_COLUMN_DEFS.filter((c) => allocationColumnVisibility[c.id]),
    [allocationColumnVisibility],
  );
  const allocationTableHeaders = useMemo(
    () => [
      ...allocationVisibleColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [allocationVisibleColumns],
  );
  const columnFilterOptions = useMemo(
    () => COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );
  const valueFilterOptions = useMemo(() => {
    if (!filterColumn) return [];
    const values = [
      ...new Set(
        (rows || []).map((row) => {
          const current = row?.[filterColumn];
          if (current == null || current === "") return "";
          if (filterColumn === "donation_date") return formatDateCell(current);
          if (filterColumn === "estimated_value") return formatMoney(current);
          if (filterColumn === "donation_verified") return donationVerifiedLabel(row);
          return String(current);
        }),
      ),
    ]
      .map((v) => String(v).trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return values.map((v) => ({ value: v, label: v }));
  }, [rows, filterColumn]);
  const allocationColumnOptions = useMemo(
    () => ALLOCATION_COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );
  const allocationValueOptions = useMemo(() => {
    if (!allocationFilterColumn) return [];
    const values = [
      ...new Set(
        (allocations || []).map((row) => {
          const current = row?.[allocationFilterColumn];
          if (current == null || current === "") return "";
          if (allocationFilterColumn === "allocation_date") return formatDateCell(current);
          return String(current);
        }),
      ),
    ]
      .map((v) => String(v).trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return values.map((v) => ({ value: v, label: v }));
  }, [allocations, allocationFilterColumn]);
  const filteredRows = useMemo(() => {
    if (!filterColumn || !filterValue) return rows;
    return (rows || []).filter((row) => {
      const current = row?.[filterColumn];
      if (current == null || current === "") return false;
      if (filterColumn === "donation_date") return formatDateCell(current) === filterValue;
      if (filterColumn === "estimated_value") return formatMoney(current) === filterValue;
      if (filterColumn === "donation_verified") return donationVerifiedLabel(row) === filterValue;
      return String(current).trim() === String(filterValue).trim();
    });
  }, [rows, filterColumn, filterValue]);
  const filteredAllocations = useMemo(() => {
    if (!allocationFilterColumn || !allocationFilterValue) return allocations;
    return (allocations || []).filter((row) => {
      const current = row?.[allocationFilterColumn];
      if (current == null || current === "") return false;
      if (allocationFilterColumn === "allocation_date") return formatDateCell(current) === allocationFilterValue;
      return String(current).trim() === String(allocationFilterValue).trim();
    });
  }, [allocations, allocationFilterColumn, allocationFilterValue]);
  const allocationTotalPages = useMemo(
    () => Math.max(1, Math.ceil((filteredAllocations.length || 0) / allocationPagination.limit)),
    [filteredAllocations.length, allocationPagination.limit],
  );
  const pagedAllocations = useMemo(() => {
    const start = (allocationPagination.page - 1) * allocationPagination.limit;
    return filteredAllocations.slice(start, start + allocationPagination.limit);
  }, [filteredAllocations, allocationPagination.page, allocationPagination.limit]);
  const selectedAllocationEquipment = useMemo(
    () => (rows || []).find((r) => String(r.id) === String(allocationForm.equipment_id || "")) || null,
    [rows, allocationForm.equipment_id],
  );

  const fetchLookups = useCallback(async () => {
    const res = await getDonorEquipmentLookups();
    setLookups(res?.data || { categories: [], conditions: [], statuses: [], master_statuses: [], donors: [] });
  }, []);

  const fetchMasters = useCallback(async () => {
    try {
      const [catRes, condRes, statRes] = await Promise.all([
        getEquipmentCategoryMasters({ scope: "master" }),
        getEquipmentConditionMasters({ scope: "master" }),
        getEquipmentStatusMasters({ scope: "master" }),
      ]);
      setCategoriesMasterRows(catRes?.data || []);
      setConditionsMasterRows(condRes?.data || []);
      setStatusesMasterRows(statRes?.data || []);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await getDonorEquipmentDashboard({
        from_date: filters.from_date,
        to_date: filters.to_date,
      });
      setDashboard(res?.data || {});
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingDashboard(false);
    }
  }, [filters.from_date, filters.to_date]);

  const fetchRows = useCallback(async () => {
    if (embedMode === "donations-collect") {
      setLoadingRows(false);
      return;
    }
    setLoadingRows(true);
    setErrorMsg("");
    try {
      const res = await getDonorEquipmentList({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setRows(res?.data || []);
      setPagination((prev) => ({
        ...prev,
        total: Number(res?.pagination?.total || 0),
      }));
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingRows(false);
    }
  }, [embedMode, filters, pagination.page, pagination.limit]);

  const fetchAllocations = useCallback(async (equipmentId) => {
    setLoadingAllocations(true);
    try {
      const res = await getEquipmentAllocations(equipmentId);
      setAllocations(res?.data || []);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingAllocations(false);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
    if (!embedMode) fetchMasters();
  }, [fetchLookups, fetchMasters, embedMode]);

  useEffect(() => {
    if (!embedMode) {
      fetchDashboard();
    } else {
      setLoadingDashboard(false);
    }
    if (embedMode !== "donations-collect") {
      fetchRows();
    } else {
      setLoadingRows(false);
    }
  }, [fetchDashboard, fetchRows, embedMode]);

  useEffect(() => {
    if (embedMode) return;
    fetchAllocations(allocationForm.equipment_id || undefined);
  }, [allocationForm.equipment_id, fetchAllocations, embedMode]);

  useEffect(() => {
    setAllocationPagination((prev) =>
      prev.page > allocationTotalPages ? { ...prev, page: allocationTotalPages } : prev,
    );
  }, [allocationTotalPages]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
      if (!allocationPickerRef.current?.contains(e.target)) setAllocationColumnPickerOpen(false);
    };
    if (columnPickerOpen || allocationColumnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen, allocationColumnPickerOpen]);

  const withCsrf = async () => {
    let csrfToken = localStorage.getItem("csrf-token");
    if (!csrfToken) {
      csrfToken = await getCsrfToken();
      if (csrfToken) localStorage.setItem("csrf-token", csrfToken);
    }
    return csrfToken;
  };

  const scrollToSection = (elementId) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  };

  const onSubmitEquipment = async (e) => {
    e.preventDefault();
    const errors = validateEquipmentForm(formData);
    setEquipmentErrors(errors);
    setFormError("");
    if (Object.keys(errors).length > 0) {
      setFormError("Please check the highlighted fields below.");
      return;
    }
    setSubmittingEquipment(true);
    try {
      const csrf = await withCsrf();
      const body = {
        donor_id: Number(formData.donor_id),
        donation_id: formData.donation_id ? Number(formData.donation_id) : null,
        donation_date: formData.donation_date,
        equipment_name: formData.equipment_name.trim(),
        category_id: Number(formData.category_id),
        quantity: Number(formData.quantity),
        estimated_value: Number(formData.estimated_value),
        brand: formData.brand?.trim() || "",
        model: formData.model?.trim() || "",
        condition_id: Number(formData.condition_id),
        location: formData.location?.trim() || "",
        equipment_status_id: Number(formData.equipment_status_id),
        remarks: formData.remarks?.trim() || "",
      };
      if (editingRow?.id) {
        await updateDonorEquipment(editingRow.id, body, csrf);
        setSuccessMsg("Equipment updated successfully.");
      } else {
        await createDonorEquipment(body, csrf);
        setSuccessMsg("Equipment created successfully.");
      }
      setEditingRow(null);
      setFormData(EMPTY_EQUIPMENT_FORM);
      setEquipmentErrors({});
      setFormError("");
      if (!embedMode) fetchDashboard();
      if (embedMode !== "donations-collect") {
        await fetchRows();
      }
      if (!embedMode) fetchAllocations(allocationForm.equipment_id || undefined);
      onEquipmentDataChanged?.();
    } catch (err) {
      setEquipmentErrors(err?.details || {});
      setFormError(safeErrorText(err));
    } finally {
      setSubmittingEquipment(false);
    }
  };

  const onSubmitAllocation = async (e) => {
    e.preventDefault();
    const errors = validateAllocationForm(allocationForm);
    setAllocationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSubmittingAllocation(true);
    try {
      const csrf = await withCsrf();
      const requestedQty = Number(allocationForm.quantity || 0);
      const actualQty = Number(
        (selectedAllocationEquipment?.available_quantity ?? selectedAllocationEquipment?.quantity) || 0,
      );
      if (selectedAllocationEquipment && requestedQty > actualQty) {
        setAllocationErrors((prev) => ({
          ...prev,
          quantity: `Please select below the actual quantity (${actualQty})`,
        }));
        return;
      }
      const payload = {
        equipment_id: Number(allocationForm.equipment_id),
        quantity: Number(allocationForm.quantity),
        assigned_to: allocationForm.assigned_to.trim(),
        allocation_date: allocationForm.allocation_date,
        remarks: allocationForm.remarks?.trim() || "",
      };
      if (editingAllocationId) {
        await updateEquipmentAllocation(editingAllocationId, payload, csrf);
        setSuccessMsg("Equipment allocation updated successfully.");
      } else {
        await createEquipmentAllocation(payload, csrf);
        setSuccessMsg("Equipment allocation saved successfully.");
      }
      setEditingAllocationId(null);
      setAllocationForm(EMPTY_ALLOCATION_FORM);
      setAllocationErrors({});
      fetchRows();
      fetchAllocations(allocationForm.equipment_id || undefined);
    } catch (err) {
      setAllocationErrors(err?.details || {});
      setErrorMsg(safeErrorText(err));
    } finally {
      setSubmittingAllocation(false);
    }
  };

  const onRequestEdit = (row) => {
    const donor = (lookups.donors || []).find((d) => Number(d.id) === Number(row?.donor_id));
    setEditingRow(row);
    setFormData({
      donor_id: row?.donor_id != null ? String(row.donor_id) : "",
      donor_name_input: donor ? `${donor.donor_name} (${donor.donor_code || donor.id})` : "",
      donation_id: row?.donation_id != null ? String(row.donation_id) : "",
      donation_date: String(row?.donation_date || "").slice(0, 10) || format(new Date(), "yyyy-MM-dd"),
      equipment_name: row?.equipment_name ?? "",
      category_id: row?.category_id != null ? String(row.category_id) : "",
      quantity: row?.quantity != null ? String(row.quantity) : "",
      estimated_value: row?.estimated_value != null ? String(row.estimated_value) : "",
      brand: row?.brand ?? "",
      model: row?.model ?? "",
      condition_id: row?.condition_id != null ? String(row.condition_id) : "",
      location: row?.location ?? "",
      equipment_status_id: row?.equipment_status_id != null ? String(row.equipment_status_id) : "",
      remarks: row?.remarks ?? "",
    });
    if (!embedMode || embedMode === "donations-tracking") {
      scrollToSection("equipment-form");
    }
  };

  const onDeactivate = async (row) => {
    try {
      const csrf = await withCsrf();
      await deleteDonorEquipment(row.id, csrf);
      setSuccessMsg("Equipment deactivated successfully.");
      if (!embedMode) fetchDashboard();
      if (embedMode !== "donations-collect") {
        await fetchRows();
      }
      if (!embedMode) fetchAllocations(allocationForm.equipment_id || undefined);
      onEquipmentDataChanged?.();
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  };
  const toggleEquipmentSelection = (rowId, checked) => {
    const key = String(rowId);
    setSelectedEquipmentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };
  const runBulkEquipmentDonationVerified = async (verified) => {
    const ids = Array.from(selectedEquipmentIds)
      .map((id) => Number(id))
      .filter((n) => Number.isInteger(n) && n > 0);
    if (ids.length === 0) return;
    setBulkMarkingEquipment(true);
    setErrorMsg("");
    try {
      const csrf = await withCsrf();
      await bulkSetEquipmentDonationVerified(ids, verified, csrf);
      setSuccessMsg(verified ? "Selected equipments marked as verified." : "Selected equipments marked as not verified.");
      setSelectedEquipmentIds(new Set());
      await fetchRows();
      onEquipmentDataChanged?.();
    } catch (err) {
      setErrorMsg(safeErrorText(err, "Failed to update equipment donation verification."));
    } finally {
      setBulkMarkingEquipment(false);
    }
  };
  const onEditAllocation = (row) => {
    setEditingAllocationId(row.id);
    setAllocationForm({
      equipment_id: row?.equipment_id != null ? String(row.equipment_id) : "",
      equipment_name_input: `${row?.equipment_name || "Equipment"} (${row?.equipment_code || row?.equipment_id || row?.id})`,
      quantity: row?.quantity != null ? String(row.quantity) : "",
      assigned_to: row?.assigned_to || "",
      allocation_date: String(row?.allocation_date || "").slice(0, 10) || format(new Date(), "yyyy-MM-dd"),
      remarks: row?.remarks || "",
    });
    setAllocationErrors({});
    scrollToSection("equipment-allocation");
  };
  const onDeleteAllocation = async (row) => {
    try {
      const csrf = await withCsrf();
      await deleteEquipmentAllocation(row.id, csrf);
      setSuccessMsg("Equipment allocation deleted successfully.");
      if (editingAllocationId && Number(editingAllocationId) === Number(row.id)) {
        setEditingAllocationId(null);
        setAllocationForm(EMPTY_ALLOCATION_FORM);
      }
      fetchAllocations(allocationForm.equipment_id || undefined);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  };

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit));
  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = COLUMN_DEFS.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        // ignore localStorage errors
      }
      return next;
    });
  };
  const toggleAllocationColumn = (id) => {
    setAllocationColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = ALLOCATION_COLUMN_DEFS.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      return next;
    });
  };

  const saveCategoryMaster = async () => {
    const name = String(categoryMaster.category_name || "").trim();
    if (!name || !categoryMaster.status_id) {
      setCategoryMasterErrors({
        ...(name ? {} : { category_name: "Category name is required" }),
        ...(categoryMaster.status_id ? {} : { status_id: "Status is required" }),
      });
      return;
    }
    try {
      setMasterSubmitting(true);
      setCategoryMasterErrors({});
      const csrf = await withCsrf();
      const res = categoryMaster.id
        ? await updateEquipmentCategoryMaster(
          categoryMaster.id,
          {
            category_name: name,
            description: categoryMaster.description?.trim() || null,
            status_id: Number(categoryMaster.status_id),
          },
          csrf,
        )
        : await createEquipmentCategoryMaster({
          category_name: name,
          description: categoryMaster.description?.trim() || null,
          status_id: Number(categoryMaster.status_id),
        }, csrf);
      setCategoryMaster({ id: null, category_name: "", description: "", status_id: "1" });
      setSuccessMsg(res?.reactivated ? "Equipment category restored." : categoryMaster.id ? "Equipment category updated." : "Equipment category created.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setCategoryMasterErrors(err?.details || {});
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  const removeCategoryMaster = async (id) => {
    try {
      setMasterSubmitting(true);
      const csrf = await withCsrf();
      await deleteEquipmentCategoryMaster(id, csrf);
      setSuccessMsg("Equipment category deleted.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  const saveConditionMaster = async () => {
    const name = String(conditionMaster.condition_name || "").trim();
    if (!name || !conditionMaster.status_id) {
      setConditionMasterErrors({
        ...(name ? {} : { condition_name: "Condition name is required" }),
        ...(conditionMaster.status_id ? {} : { status_id: "Status is required" }),
      });
      return;
    }
    try {
      setMasterSubmitting(true);
      setConditionMasterErrors({});
      const csrf = await withCsrf();
      const res = conditionMaster.id
        ? await updateEquipmentConditionMaster(
          conditionMaster.id,
          {
            condition_name: name,
            description: conditionMaster.description?.trim() || null,
            status_id: Number(conditionMaster.status_id),
          },
          csrf,
        )
        : await createEquipmentConditionMaster({
          condition_name: name,
          description: conditionMaster.description?.trim() || null,
          status_id: Number(conditionMaster.status_id),
        }, csrf);
      setConditionMaster({ id: null, condition_name: "", description: "", status_id: "1" });
      setSuccessMsg(res?.reactivated ? "Equipment condition restored." : conditionMaster.id ? "Equipment condition updated." : "Equipment condition created.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setConditionMasterErrors(err?.details || {});
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  const removeConditionMaster = async (id) => {
    try {
      setMasterSubmitting(true);
      const csrf = await withCsrf();
      await deleteEquipmentConditionMaster(id, csrf);
      setSuccessMsg("Equipment condition deleted.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  const saveStatusMaster = async () => {
    const name = String(statusMaster.status_name || "").trim();
    if (!name || !statusMaster.status_id) {
      setStatusMasterErrors({
        ...(name ? {} : { status_name: "Status name is required" }),
        ...(statusMaster.status_id ? {} : { status_id: "Status is required" }),
      });
      return;
    }
    try {
      setMasterSubmitting(true);
      setStatusMasterErrors({});
      const csrf = await withCsrf();
      const res = statusMaster.id
        ? await updateEquipmentStatusMaster(statusMaster.id, { status_name: name, status_id: Number(statusMaster.status_id) }, csrf)
        : await createEquipmentStatusMaster({ status_name: name, status_id: Number(statusMaster.status_id) }, csrf);
      setStatusMaster({ id: null, status_name: "", status_id: "1" });
      setSuccessMsg(res?.reactivated ? "Equipment status restored." : statusMaster.id ? "Equipment status updated." : "Equipment status created.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setStatusMasterErrors(err?.details || {});
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  const removeStatusMaster = async (id) => {
    try {
      setMasterSubmitting(true);
      const csrf = await withCsrf();
      await deleteEquipmentStatusMaster(id, csrf);
      setSuccessMsg("Equipment status deleted.");
      await Promise.all([fetchMasters(), fetchLookups()]);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setMasterSubmitting(false);
    }
  };

  return (
    <div className={hideEmbeddedChrome ? "donor-equipment-embed" : "user-builder-shell"}>
      {!hideEmbeddedChrome ? (
        <header className="user-builder-hero">
          <div className="user-builder-hero__text">
            <p className="user-builder-hero__eyebrow">Donor Support</p>
            <h2 className="user-builder-hero__title">Donor equipment list</h2>
          </div>
        </header>
      ) : null}
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className={hideEmbeddedChrome ? "" : "user-builder-layout"}>
        <div className={hideEmbeddedChrome ? "" : "user-builder-layout__main"}>
          {!hideEquipmentStatsPanel ? (
          <div id="equipment-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">Dashboard summary</h3></div>
            <GlobalMetricCards
              loading={loadingDashboard}
              ariaLabel="Donor equipment summary"
              items={[
                { key: "total_equipment_donations", label: "Total equipment donations", value: dashboard?.total_equipment_donations ?? 0, tone: "slate" },
                { key: "total_equipment_quantity", label: "Total quantity", value: dashboard?.total_equipment_quantity ?? 0, tone: "teal" },
                { key: "total_equipment_value", label: "Total value", value: `₹${dashboard?.total_equipment_value ?? 0}`, tone: "mint" },
                { key: "equipment_this_month", label: "Equipments this month", value: dashboard?.equipment_this_month ?? 0, tone: "coral" },
              ]}
            />
          </div>
          ) : null}

          {!hideEquipmentFormPanel ? (
          <div id="equipment-form" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">{editingRow ? "Edit equipment donation" : "Add equipment donation"}</h3></div>
            <form onSubmit={onSubmitEquipment} noValidate>
              <GlobalFormShell
                legend={editingRow ? "Update equipment details" : "New equipment details"}
                showAlert={Boolean(formError)}
                alertText={formError}
                alertId={ALERT_ID}
                overflowVisible
                actions={(
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <GlobalButton type="submit" variant="primary" disabled={submittingEquipment}>
                      {submittingEquipment ? "Saving..." : editingRow ? "Update Equipment" : "Save Equipment"}
                    </GlobalButton>
                    {editingRow ? (
                      <GlobalButton type="button" variant="ghost" onClick={() => { setEditingRow(null); setFormData(EMPTY_EQUIPMENT_FORM); setEquipmentErrors({}); setFormError(""); }}>
                        Cancel
                      </GlobalButton>
                    ) : null}
                  </div>
                )}
              >
                <div className="gff-field gff-field--full" style={{ position: "relative" }}>
                  <GlobalTextField
                    label="Donor name"
                    value={formData.donor_name_input}
                    onChange={(v) => setFormData((p) => ({ ...p, donor_name_input: v, donor_id: "" }))}
                    placeholder="Type donor name / code"
                    error={equipmentErrors.donor_id}
                    baseAlertId={ALERT_ID}
                    required
                  />
                  {formData.donor_name_input && donorSuggestionList.length > 0 ? (
                    <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", maxHeight: "180px", overflowY: "auto" }}>
                      {donorSuggestionList.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              donor_id: String(d.id),
                              donor_name_input: `${d.donor_name} (${d.donor_code || d.id})`,
                            }))}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: 0, background: "#fff" }}
                        >
                          {d.donor_name} ({d.donor_code || d.id})
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <GlobalDateField label="Donation date" value={formData.donation_date} onChange={(v) => setFormData((p) => ({ ...p, donation_date: v }))} error={equipmentErrors.donation_date} baseAlertId={ALERT_ID} required />
                <GlobalTextField label="Equipment name" value={formData.equipment_name} onChange={(v) => setFormData((p) => ({ ...p, equipment_name: v.replace(/\s+/g, " ") }))} error={equipmentErrors.equipment_name} baseAlertId={ALERT_ID} required />
                <GlobalSelectField label="Category" value={formData.category_id} onChange={(v) => setFormData((p) => ({ ...p, category_id: v }))} options={categoryOptions} placeholderOption="—" error={equipmentErrors.category_id} baseAlertId={ALERT_ID} required />
                <GlobalTextField label="Quantity" value={formData.quantity} onChange={(v) => setFormData((p) => ({ ...p, quantity: sanitizePositiveIntegerInput(v) }))} inputMode="numeric" error={equipmentErrors.quantity} baseAlertId={ALERT_ID} required />
                <GlobalTextField label="Estimated value (₹)" value={formData.estimated_value} onChange={(v) => setFormData((p) => ({ ...p, estimated_value: sanitizePositiveNumberInput(v) }))} inputMode="decimal" error={equipmentErrors.estimated_value} baseAlertId={ALERT_ID} required />
                <GlobalTextField label="Brand" value={formData.brand} onChange={(v) => setFormData((p) => ({ ...p, brand: v }))} />
                <GlobalTextField label="Model" value={formData.model} onChange={(v) => setFormData((p) => ({ ...p, model: v }))} />
                <GlobalSelectField label="Condition" value={formData.condition_id} onChange={(v) => setFormData((p) => ({ ...p, condition_id: v }))} options={conditionOptions} placeholderOption="—" error={equipmentErrors.condition_id} baseAlertId={ALERT_ID} required />
                <GlobalTextField label="Storage location" value={formData.location} onChange={(v) => setFormData((p) => ({ ...p, location: v }))} />
                <GlobalSelectField label="Equipment status" value={formData.equipment_status_id} onChange={(v) => setFormData((p) => ({ ...p, equipment_status_id: v }))} options={statusOptions} placeholderOption="—" error={equipmentErrors.equipment_status_id} baseAlertId={ALERT_ID} required />
                <GlobalTextAreaField label="Remarks" value={formData.remarks} onChange={(v) => setFormData((p) => ({ ...p, remarks: v }))} rows={2} />
              </GlobalFormShell>
            </form>
          </div>
          ) : null}

          {!hideEquipmentListPanel ? (
          <div id="equipment-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">Equipment list</h3></div>
            <div className="ub-filters ub-directory-filters" style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginTop: 0, marginBottom: "0.5rem" }}>
              <div className="ub-ms-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
                <GlobalDateField label="From date" value={filters.from_date} onChange={(v) => setFilters((p) => ({ ...p, from_date: v }))} />
                <GlobalDateField label="To date" value={filters.to_date} onChange={(v) => setFilters((p) => ({ ...p, to_date: v }))} />
                <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
                  <GlobalSelectField
                    label="Column"
                    value={filterColumn}
                    onChange={(v) => {
                      setFilterColumn(v);
                      setFilterValue("");
                    }}
                    options={columnFilterOptions}
                    placeholderOption="Select"
                  />
                </div>
                <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
                  <GlobalSelectField
                    label="Value"
                    value={filterValue}
                    onChange={setFilterValue}
                    options={valueFilterOptions}
                    placeholderOption="All"
                    disabled={!filterColumn}
                  />
                </div>
                <div style={{ flex: "0 0 auto" }}>
                  <GlobalButton
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFilters(EMPTY_FILTERS);
                      setFilterColumn("");
                      setFilterValue("");
                      setPagination((p) => ({ ...p, page: 1 }));
                    }}
                  >
                    Clear filter
                  </GlobalButton>
                </div>
              </div>
              <div className="ub-directory-table-toolbar" style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.65rem", width: "100%" }}>
                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
                  <div className="vt-column-picker" ref={pickerRef}>
                    <GlobalButton type="button" size="sm" variant="ghost" onClick={() => setColumnPickerOpen((o) => !o)}>
                      <Columns3 size={16} aria-hidden />
                      <span style={{ marginLeft: "0.35rem" }}>Columns</span>
                    </GlobalButton>
                    {columnPickerOpen ? (
                      <div id="equipment-list-column-panel" className="vt-column-picker__panel" role="group" aria-label="Choose visible columns">
                        <p className="vt-column-picker__title">Show columns</p>
                        <ul className="vt-column-picker__list">
                          {COLUMN_DEFS.map((c) => (
                            <li key={c.id}>
                              <label className="vt-column-picker__row">
                                <input type="checkbox" checked={!!columnVisibility[c.id]} onChange={() => toggleColumn(c.id)} />
                                <span>{c.label}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <GlobalButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => exportEquipmentCsv(filteredRows, visibleDataColumns)}
                  disabled={filteredRows.length === 0 || visibleDataColumns.length === 0}
                  style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap", minWidth: "110px" }}
                >
                  Export CSV
                </GlobalButton>
              </div>
            </div>

            {loadingRows ? <p className="ub-loading">Loading equipment...</p> : (
              <>
                {isAdminRoute ? (
                  <div className="bulk-actions-bar" style={{ marginBottom: "1rem" }}>
                    <div className="bulk-actions-info">
                      <span className="selection-count">Approval & Marking</span>
                      <span className="selection-hint">Select checkboxes to mark in bulk</span>
                    </div>
                    <div className="bulk-actions-buttons">
                      <GlobalButton type="button" disabled={!selectedEquipmentIds.size || bulkMarkingEquipment} onClick={() => runBulkEquipmentDonationVerified(true)}>
                        Verify Donation
                      </GlobalButton>
                      <GlobalButton type="button" disabled={!selectedEquipmentIds.size || bulkMarkingEquipment} onClick={() => runBulkEquipmentDonationVerified(false)}>
                        Unverify Donation
                      </GlobalButton>
                      <GlobalButton type="button" disabled={!selectedEquipmentIds.size} onClick={() => setSelectedEquipmentIds(new Set())}>
                        Clear Selection
                      </GlobalButton>
                    </div>
                  </div>
                ) : null}
                <GlobalDataTable
                  wrapClassName="ub-table-wrap"
                  tableClassName="ub-table"
                  tableAriaLabel="Donor equipment list"
                  headers={tableHeaders}
                >
                  {filteredRows.length === 0 ? (
                    <tr><td colSpan={Math.max(visibleDataColumns.length + (isAdminRoute ? 2 : 1), 1)} style={{ textAlign: "center", padding: "1.75rem" }}>No equipment records found.</td></tr>
                  ) : filteredRows.map((row) => (
                    <tr key={row.id}>
                      {isAdminRoute ? (
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedEquipmentIds.has(String(row.id))}
                            onChange={(e) => toggleEquipmentSelection(row.id, e.target.checked)}
                          />
                        </td>
                      ) : null}
                      {visibleDataColumns.map((col) => (
                        <td key={col.id}>
                          {col.id === "donation_date"
                            ? formatDateCell(row.donation_date)
                            : col.id === "estimated_value"
                              ? formatMoney(row.estimated_value)
                              : col.id === "donation_verified"
                                ? (
                                  <GlobalBadge
                                    active={Number(row?.donation_verified) === 1}
                                    activeText="Verified"
                                    inactiveText="Not verified"
                                  />
                                )
                              : (row[col.id] ?? "—")}
                        </td>
                      ))}
                      <td className="ub-table__col-actions">
                        <div className="ub-table-actions">
                          <GlobalTableActionButton action="edit" variant="ghost" onClick={() => onRequestEdit(row)} />
                          {!equipmentTrackingEditOnly ? (
                            <>
                              <GlobalTableActionButton
                                action="allocate"
                                variant="ghost"
                                onClick={() => {
                                  const availableQty = Number((row.available_quantity ?? row.quantity) || 0);
                                  if (availableQty < 1) {
                                    setWarningMsg("Quantity not available");
                                    return;
                                  }
                                  setAllocationForm((p) => ({ ...p, equipment_id: String(row.id), equipment_name_input: `${row.equipment_name || "Equipment"} (${row.equipment_code || row.id})` }));
                                  scrollToSection("equipment-allocation");
                                }}
                              />
                              <GlobalTableActionButton action="deactivate" variant="ghost" onClick={() => onDeactivate(row)} />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </GlobalDataTable>
                <GlobalDirectoryPaginationSection
                  page={pagination.page}
                  totalPages={totalPages}
                  total={pagination.total}
                  pageSize={pagination.limit}
                  onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
                  label="Donor equipment pagination"
                />
              </>
            )}
          </div>
          ) : null}

          {!hideAllocationAndMasters ? (
          <div id="equipment-allocation" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">Equipment allocation</h3></div>
            <form onSubmit={onSubmitAllocation} noValidate>
              <GlobalFormShell
                legend="Allocate equipment"
                actions={
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {editingAllocationId ? (
                      <GlobalButton
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setEditingAllocationId(null);
                          setAllocationForm(EMPTY_ALLOCATION_FORM);
                          setAllocationErrors({});
                        }}
                      >
                        Cancel edit
                      </GlobalButton>
                    ) : null}
                    <GlobalButton type="submit" variant="primary" disabled={submittingAllocation}>
                      {submittingAllocation ? "Saving..." : editingAllocationId ? "Update Allocation" : "Save Allocation"}
                    </GlobalButton>
                  </div>
                }
              >
                <div className="gff-field gff-field--full" style={{ position: "relative" }}>
                  <GlobalTextField
                    label="Equipment name"
                    value={allocationForm.equipment_name_input}
                    onChange={(v) => setAllocationForm((p) => ({ ...p, equipment_name_input: v, equipment_id: "" }))}
                    placeholder="Type equipment name / code"
                    error={allocationErrors.equipment_id}
                    required
                  />
                  {allocationForm.equipment_name_input && equipmentSuggestionList.length > 0 ? (
                    <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", maxHeight: "180px", overflowY: "auto" }}>
                      {equipmentSuggestionList.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            const availableQty = Number((r.available_quantity ?? r.quantity) || 0);
                            if (availableQty < 1) {
                              setWarningMsg("Quantity not available");
                              return;
                            }
                            setAllocationForm((p) => ({
                              ...p,
                              equipment_id: String(r.id),
                              equipment_name_input: `${r.equipment_name || "Equipment"} (${r.equipment_code || r.id})`,
                            }));
                          }}
                          style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: 0, background: "#fff" }}
                        >
                          {r.equipment_name || "Equipment"} ({r.equipment_code || r.id})
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <GlobalTextField label="Quantity" value={allocationForm.quantity} onChange={(v) => setAllocationForm((p) => ({ ...p, quantity: sanitizePositiveIntegerInput(v) }))} inputMode="numeric" error={allocationErrors.quantity} required />
                <GlobalTextField label="Assigned to" value={allocationForm.assigned_to} onChange={(v) => setAllocationForm((p) => ({ ...p, assigned_to: v }))} error={allocationErrors.assigned_to} required />
                <GlobalDateField label="Allocation date" value={allocationForm.allocation_date} onChange={(v) => setAllocationForm((p) => ({ ...p, allocation_date: v }))} error={allocationErrors.allocation_date} required />
                <GlobalTextAreaField label="Remarks" value={allocationForm.remarks} onChange={(v) => setAllocationForm((p) => ({ ...p, remarks: v }))} rows={2} />
              </GlobalFormShell>
            </form>

            <div style={{ marginTop: "1rem" }}>
              <div className="ub-filters ub-directory-filters" style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginTop: 0, marginBottom: "0.5rem" }}>
                <div className="ub-ms-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
                  <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
                    <GlobalSelectField
                      label="Column"
                      value={allocationFilterColumn}
                      onChange={(v) => {
                        setAllocationFilterColumn(v);
                        setAllocationFilterValue("");
                        setAllocationPagination((p) => ({ ...p, page: 1 }));
                      }}
                      options={allocationColumnOptions}
                      placeholderOption="Select"
                    />
                  </div>
                  <div style={{ flex: "0 1 220px", minWidth: "170px" }}>
                    <GlobalSelectField
                      label="Value"
                      value={allocationFilterValue}
                      onChange={(v) => {
                        setAllocationFilterValue(v);
                        setAllocationPagination((p) => ({ ...p, page: 1 }));
                      }}
                      options={allocationValueOptions}
                      placeholderOption="All"
                      disabled={!allocationFilterColumn}
                    />
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    <GlobalButton
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setAllocationFilterColumn("");
                        setAllocationFilterValue("");
                        setAllocationPagination((p) => ({ ...p, page: 1 }));
                      }}
                    >
                      Clear filter
                    </GlobalButton>
                  </div>
                </div>
                <div className="ub-directory-table-toolbar" style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.65rem", width: "100%" }}>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
                    <div className="vt-column-picker" ref={allocationPickerRef}>
                      <GlobalButton type="button" size="sm" variant="ghost" onClick={() => setAllocationColumnPickerOpen((o) => !o)}>
                        <Columns3 size={16} aria-hidden />
                        <span style={{ marginLeft: "0.35rem" }}>Columns</span>
                      </GlobalButton>
                      {allocationColumnPickerOpen ? (
                        <div id="allocation-list-column-panel" className="vt-column-picker__panel" role="group" aria-label="Choose visible allocation columns">
                          <p className="vt-column-picker__title">Show columns</p>
                          <ul className="vt-column-picker__list">
                            {ALLOCATION_COLUMN_DEFS.map((c) => (
                              <li key={c.id}>
                                <label className="vt-column-picker__row">
                                  <input type="checkbox" checked={!!allocationColumnVisibility[c.id]} onChange={() => toggleAllocationColumn(c.id)} />
                                  <span>{c.label}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <GlobalButton
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => exportAllocationCsv(filteredAllocations, allocationVisibleColumns)}
                    disabled={filteredAllocations.length === 0 || allocationVisibleColumns.length === 0}
                    style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap", minWidth: "110px" }}
                  >
                    Export CSV
                  </GlobalButton>
                </div>
              </div>
              {loadingAllocations ? <p className="ub-loading">Loading allocations...</p> : (
                <>
                  <GlobalDataTable
                    wrapClassName="ub-table-wrap"
                    tableClassName="ub-table"
                    tableAriaLabel="Equipment allocations list"
                    headers={allocationTableHeaders}
                  >
                    {pagedAllocations.length === 0 ? (
                      <tr><td colSpan={Math.max(allocationVisibleColumns.length + 1, 1)} style={{ textAlign: "center", padding: "1.75rem" }}>No allocations found.</td></tr>
                    ) : pagedAllocations.map((a) => (
                      <tr key={a.id}>
                        {allocationVisibleColumns.map((col) => (
                          <td key={col.id}>{renderAllocationCell(a, col.id)}</td>
                        ))}
                        <td className="ub-table__col-actions">
                          <div className="ub-table-actions">
                            <GlobalTableActionButton action="edit" variant="ghost" onClick={() => onEditAllocation(a)} />
                            <GlobalTableActionButton action="delete" variant="ghost" onClick={() => onDeleteAllocation(a)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </GlobalDataTable>
                  <GlobalDirectoryPaginationSection
                    page={allocationPagination.page}
                    totalPages={allocationTotalPages}
                    total={filteredAllocations.length}
                    pageSize={allocationPagination.limit}
                    onPageChange={(page) => setAllocationPagination((p) => ({ ...p, page }))}
                    label="Equipment allocation pagination"
                  />
                </>
              )}
            </div>
          </div>
          ) : null}

          {!hideAllocationAndMasters ? (
          <div id="equipment-masters" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Master table management</h3>
            </div>
            <div style={{ display: "grid", gap: "1.5rem" }}>
              <div className="ub-master-section">
                <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>Equipment category master</h3>
                <GlobalFormShell
                  legend={categoryMaster.id ? "Edit category" : "Add category"}
                  actions={(
                    <>
                      {categoryMaster.id ? (
                        <GlobalButton variant="ghost" onClick={() => { setCategoryMaster({ id: null, category_name: "", description: "", status_id: "1" }); setCategoryMasterErrors({}); }}>
                          Cancel edit
                        </GlobalButton>
                      ) : null}
                      <GlobalButton variant="primary" onClick={saveCategoryMaster} disabled={masterSubmitting}>
                        {categoryMaster.id ? "Save category" : "Create category"}
                      </GlobalButton>
                    </>
                  )}
                >
                  <GlobalTextField label="Category name" value={categoryMaster.category_name} onChange={(v) => setCategoryMaster((p) => ({ ...p, category_name: v }))} error={categoryMasterErrors.category_name} baseAlertId={ALERT_ID} required />
                  <GlobalTextAreaField label="Description" value={categoryMaster.description} onChange={(v) => setCategoryMaster((p) => ({ ...p, description: v }))} rows={2} />
                  <GlobalSelectField label="Record status" value={categoryMaster.status_id} onChange={(v) => setCategoryMaster((p) => ({ ...p, status_id: v }))} options={masterStatusOptions} placeholderOption="—" error={categoryMasterErrors.status_id} baseAlertId={ALERT_ID} required />
                </GlobalFormShell>
                <GlobalDataTable wrapClassName="ub-table-wrap" tableClassName="ub-table" tableAriaLabel="Equipment category masters" headers={[{ key: "category_name", label: "Category" }, { key: "description", label: "Description" }, { key: "status_id", label: "Record status" }, { key: "actions", label: "Actions", className: "ub-table__col-actions" }]}>
                  {categoriesMasterRows.filter((r) => Number(r.is_active) === 1).length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: "center", padding: "1.5rem" }}>No category records.</td></tr>
                  ) : categoriesMasterRows.filter((r) => Number(r.is_active) === 1).map((r) => (
                    <tr key={r.id}>
                      <td>{r.category_name}</td>
                      <td>{r.description || "—"}</td>
                      <td>
                        <MasterRecordStatusBadge statusId={r.status_id} statusLabel={r.status_name} />
                      </td>
                      <td className="ub-table__col-actions">
                        <div className="ub-table-actions">
                          <GlobalTableActionButton
                            action="edit"
                            variant="ghost"
                            onClick={() => { setCategoryMaster({ id: r.id, category_name: r.category_name || "", description: r.description || "", status_id: r.status_id != null ? String(r.status_id) : "1" }); setCategoryMasterErrors({}); }}
                          />
                          <GlobalTableActionButton action="delete" variant="ghost" onClick={() => removeCategoryMaster(r.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </GlobalDataTable>
              </div>

              <div className="ub-master-section">
                <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>Equipment condition master</h3>
                <GlobalFormShell
                  legend={conditionMaster.id ? "Edit condition" : "Add condition"}
                  actions={(
                    <>
                      {conditionMaster.id ? (
                        <GlobalButton variant="ghost" onClick={() => { setConditionMaster({ id: null, condition_name: "", description: "", status_id: "1" }); setConditionMasterErrors({}); }}>
                          Cancel edit
                        </GlobalButton>
                      ) : null}
                      <GlobalButton variant="primary" onClick={saveConditionMaster} disabled={masterSubmitting}>
                        {conditionMaster.id ? "Save condition" : "Create condition"}
                      </GlobalButton>
                    </>
                  )}
                >
                  <GlobalTextField label="Condition name" value={conditionMaster.condition_name} onChange={(v) => setConditionMaster((p) => ({ ...p, condition_name: v }))} error={conditionMasterErrors.condition_name} baseAlertId={ALERT_ID} required />
                  <GlobalTextAreaField label="Description" value={conditionMaster.description} onChange={(v) => setConditionMaster((p) => ({ ...p, description: v }))} rows={2} />
                  <GlobalSelectField label="Record status" value={conditionMaster.status_id} onChange={(v) => setConditionMaster((p) => ({ ...p, status_id: v }))} options={masterStatusOptions} placeholderOption="—" error={conditionMasterErrors.status_id} baseAlertId={ALERT_ID} required />
                </GlobalFormShell>
                <GlobalDataTable wrapClassName="ub-table-wrap" tableClassName="ub-table" tableAriaLabel="Equipment condition masters" headers={[{ key: "condition_name", label: "Condition" }, { key: "description", label: "Description" }, { key: "status_id", label: "Record status" }, { key: "actions", label: "Actions", className: "ub-table__col-actions" }]}>
                  {conditionsMasterRows.filter((r) => Number(r.is_active) === 1).length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: "center", padding: "1.5rem" }}>No condition records.</td></tr>
                  ) : conditionsMasterRows.filter((r) => Number(r.is_active) === 1).map((r) => (
                    <tr key={r.id}>
                      <td>{r.condition_name}</td>
                      <td>{r.description || "—"}</td>
                      <td>
                        <MasterRecordStatusBadge statusId={r.status_id} statusLabel={r.status_name} />
                      </td>
                      <td className="ub-table__col-actions">
                        <div className="ub-table-actions">
                          <GlobalTableActionButton
                            action="edit"
                            variant="ghost"
                            onClick={() => { setConditionMaster({ id: r.id, condition_name: r.condition_name || "", description: r.description || "", status_id: r.status_id != null ? String(r.status_id) : "1" }); setConditionMasterErrors({}); }}
                          />
                          <GlobalTableActionButton action="delete" variant="ghost" onClick={() => removeConditionMaster(r.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </GlobalDataTable>
              </div>

              <div className="ub-master-section">
                <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>Equipment status master</h3>
                <GlobalFormShell
                  legend={statusMaster.id ? "Edit status" : "Add status"}
                  actions={(
                    <>
                      {statusMaster.id ? (
                        <GlobalButton variant="ghost" onClick={() => { setStatusMaster({ id: null, status_name: "", status_id: "1" }); setStatusMasterErrors({}); }}>
                          Cancel edit
                        </GlobalButton>
                      ) : null}
                      <GlobalButton variant="primary" onClick={saveStatusMaster} disabled={masterSubmitting}>
                        {statusMaster.id ? "Save status" : "Create status"}
                      </GlobalButton>
                    </>
                  )}
                >
                  <GlobalTextField label="Status name" value={statusMaster.status_name} onChange={(v) => setStatusMaster((p) => ({ ...p, status_name: v }))} error={statusMasterErrors.status_name} baseAlertId={ALERT_ID} required />
                  <GlobalSelectField label="Record status" value={statusMaster.status_id} onChange={(v) => setStatusMaster((p) => ({ ...p, status_id: v }))} options={masterStatusOptions} placeholderOption="—" error={statusMasterErrors.status_id} baseAlertId={ALERT_ID} required />
                </GlobalFormShell>
                <GlobalDataTable wrapClassName="ub-table-wrap" tableClassName="ub-table" tableAriaLabel="Equipment status masters" headers={[{ key: "status_name", label: "Status name" }, { key: "status_id", label: "Record status" }, { key: "actions", label: "Actions", className: "ub-table__col-actions" }]}>
                  {statusesMasterRows.filter((r) => Number(r.is_active) === 1).length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", padding: "1.5rem" }}>No status records.</td></tr>
                  ) : statusesMasterRows.filter((r) => Number(r.is_active) === 1).map((r) => (
                    <tr key={r.id}>
                      <td>{r.status_name}</td>
                      <td>
                        <MasterRecordStatusBadge
                          statusId={r.status_id}
                          statusLabel={r.state_status_name}
                        />
                      </td>
                      <td className="ub-table__col-actions">
                        <div className="ub-table-actions">
                          <GlobalTableActionButton
                            action="edit"
                            variant="ghost"
                            onClick={() => { setStatusMaster({ id: r.id, status_name: r.status_name || "", status_id: r.status_id != null ? String(r.status_id) : "1" }); setStatusMasterErrors({}); }}
                          />
                          <GlobalTableActionButton action="delete" variant="ghost" onClick={() => removeStatusMaster(r.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </GlobalDataTable>
              </div>
            </div>
          </div>
          ) : null}
        </div>

        {!hideEmbeddedChrome ? (
        <GlobalQuickJump asideAriaLabel="Donor equipment sections" items={QUICK_JUMP_ITEMS} activeKey={activeQuickJump} onActiveChange={setActiveQuickJump} />
        ) : null}
      </div>
    </div>
  );
}
